import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { updateLanguage } from "../api/auth";
import fr from "../translations/fr";
import { AuthContext } from "./AuthContext";

export const LanguageContext = createContext();

const supportedLanguages = ["fr"];
const normalizeLanguage = (language) =>
  supportedLanguages.includes(language) ? language : "fr";

export const LanguageProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [lang, setLang] = useState(() =>
    normalizeLanguage(localStorage.getItem("teqa-lang") || "fr")
  );

  const applyLang = useCallback((language) => {
    const nextLanguage = normalizeLanguage(language);
    setLang(nextLanguage);
    localStorage.setItem("teqa-lang", nextLanguage);
    localStorage.removeItem("lang");
    document.documentElement.lang = nextLanguage;
    document.documentElement.dir = "ltr";
  }, []);

  useEffect(() => {
    applyLang(user?.language || lang);
  }, [applyLang, lang, user?.language]);

  const t = useCallback((key, fallback = "") => {
    try {
      const value = key.split(".").reduce((object, part) => object?.[part], fr);
      return value ?? fallback ?? key;
    } catch {
      return fallback || key;
    }
  }, []);

  const changeLanguage = useCallback(async (language) => {
    const nextLanguage = normalizeLanguage(language);
    if (language !== nextLanguage) return false;
    applyLang(nextLanguage);
    if (user && user.language !== nextLanguage) {
      try {
        await updateLanguage({ language: nextLanguage });
      } catch {
        return false;
      }
    }
    return true;
  }, [applyLang, user]);

  const value = useMemo(() => ({
    lang,
    locale: lang,
    dir: "ltr",
    isRTL: false,
    t,
    setLocale: changeLanguage,
    changeLanguage,
    supportedLanguages,
  }), [changeLanguage, lang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
